import React, {
  forwardRef,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import styled, { css, ThemeContext } from 'styled-components'

// Styles
import { shadows } from '../../styles/effects'
import { applyTextStyle, applyTextEllipsis } from '../../styles/typography'

// Components
import Link from '../Link'
import Text, { MarkDownText } from '../Text'
import Avatar, { AvatarSize } from '../Avatar'
import CircleIcon from '../icon/CircleIcon'
import MarkdownToolbar, { IMarkdownActiveMarks } from './MarkdownToolbar'

// Utils
import {
  BlockMark,
  InlineMark,
  MarkSelection,
  isBlockMarkActive,
  isInlineMarkActive,
  toggleBlockMark,
  toggleInlineMark,
} from '../../utils/markdown'

// Hooks
import { useAutoGrowTextarea } from '../../hooks/useAutoGrowTextarea'

// Types
import { IFormControlProps } from '../../common/models/IFormControlProps'
import { ICompanyMember } from '../../common/models/Company'
import { IconType } from '../icon/IconType'

export type TextareaSize = 'default' | 'large' | 'xlarge'

// Min/max height in px per semantic size. The field auto-grows from min and
// scrolls (and stops manual resizing) at max.
const SIZES: Record<TextareaSize, { minHeight: number; maxHeight: number }> = {
  default: { minHeight: 48, maxHeight: 128 },
  large: { minHeight: 128, maxHeight: 290 },
  xlarge: { minHeight: 196, maxHeight: 460 },
}

export interface IProps extends IFormControlProps {
  placeholder?: string
  className?: string
  size?: TextareaSize
  height?: number // Legacy fixed/min height in px — prefer `size`.
  handleBlur?: (e: any) => void
  button?: {
    label: string
    handleClick: () => void
    disabled?: boolean
  }
  user?: ICompanyMember
  maxLength?: number
  iconType?: IconType
  iconHoverText?: string
  coloredBackground?: boolean
  // Enables a markdown formatting toolbar above the field.
  markdown?: boolean
  // Shows the heading (H1) button in the markdown toolbar (hidden by default).
  markdownHeading?: boolean
  // Puts the markdown toolbar under the field instead of above it, so opening
  // it in a field that only shows its toolbar while focused does not push the
  // text down.
  markdownToolbarBottom?: boolean
  // Drops the markdown container's form tint for a plain white field.
  markdownPlain?: boolean
  // Cypress handle, forwarded to the <textarea> with the rest of the props.
  'data-cy'?: string
}

const Textarea = forwardRef<HTMLTextAreaElement, IProps>(
  (
    {
      height,
      size,
      className,
      user,
      markdown,
      markdownHeading,
      markdownToolbarBottom,
      markdownPlain,
      ...props
    },
    ref
  ) => {
    const themeContext = useContext(ThemeContext)

    const { minHeight, maxHeight } = size
      ? SIZES[size]
      : { minHeight: height ?? 128, maxHeight: Math.max(height ?? 128, 460) }
    const setAutoGrowNode = useAutoGrowTextarea(
      props.value,
      minHeight,
      maxHeight
    )

    // Own reference to the underlying <textarea>, merged with any forwarded
    // ref, so the markdown toolbar and auto-grow hook can read it.
    const innerRef = useRef<HTMLTextAreaElement | null>(null)
    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node
        setAutoGrowNode(node)
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref, setAutoGrowNode]
    )

    const [preview, setPreview] = useState(false)
    const [marks, setMarks] = useState<IMarkdownActiveMarks>({
      bold: false,
      italic: false,
      heading: false,
      bullet: false,
      numbered: false,
    })
    const pendingSelectionRef = useRef<{ start: number; end: number } | null>(
      null
    )

    const recomputeMarks = useCallback(() => {
      const node = innerRef.current
      if (!node) return
      const selection: MarkSelection = {
        value: node.value,
        selectionStart: node.selectionStart ?? 0,
        selectionEnd: node.selectionEnd ?? 0,
      }
      setMarks({
        bold: isInlineMarkActive(selection, 'bold'),
        italic: isInlineMarkActive(selection, 'italic'),
        heading: isBlockMarkActive(selection, 'heading'),
        bullet: isBlockMarkActive(selection, 'bullet'),
        numbered: isBlockMarkActive(selection, 'numbered'),
      })
    }, [])

    const applyResult = (result: MarkSelection) => {
      pendingSelectionRef.current = {
        start: result.selectionStart,
        end: result.selectionEnd,
      }
      // Synthetic event understood by Formik, react-hook-form and plain
      // useState callers (some of which call e.persist()).
      props.onChange?.({
        target: { value: result.value, name: (props as any).name },
        currentTarget: { value: result.value, name: (props as any).name },
        persist: () => undefined,
      } as any)
    }

    const readSelection = (): MarkSelection | null => {
      const node = innerRef.current
      if (!node) return null
      return {
        value: node.value,
        selectionStart: node.selectionStart ?? 0,
        selectionEnd: node.selectionEnd ?? 0,
      }
    }

    const handleInline = (mark: InlineMark) => {
      const selection = readSelection()
      if (selection) applyResult(toggleInlineMark(selection, mark))
    }
    const handleBlock = (mark: BlockMark) => {
      const selection = readSelection()
      if (selection) applyResult(toggleBlockMark(selection, mark))
    }

    const handleTogglePreview = () => {
      // Switching into preview unmounts the textarea, so its blur-driven
      // autosave may be skipped. Flush it explicitly with the current value.
      if (!preview && innerRef.current) {
        props.handleBlur?.({
          target: { value: innerRef.current.value, name: (props as any).name },
          currentTarget: {
            value: innerRef.current.value,
            name: (props as any).name,
          },
        } as any)
      }
      setPreview((value) => !value)
    }

    // Restore the intended selection after the controlled value updated.
    useLayoutEffect(() => {
      if (!markdown) return
      const node = innerRef.current
      const pending = pendingSelectionRef.current
      if (node && pending) {
        node.focus()
        node.setSelectionRange(pending.start, pending.end)
        pendingSelectionRef.current = null
        recomputeMarks()
      }
    }, [props.value, markdown, recomputeMarks])

    const errorMessage =
      props.meta && props.meta.error && props.meta.touched
        ? props.meta.error
        : props.errorMessage

    const inner = (
      <InnerWrapper>
        {user && <StyledAvatar user={user} size={AvatarSize.Small} />}
        {markdown && preview ? (
          <PreviewBox style={{ minHeight }}>
            <MarkDownText textStyle="bodySmall">
              {props.value != null ? String(props.value) : ''}
            </MarkDownText>
          </PreviewBox>
        ) : (
          <StyledTextarea
            ref={setRefs}
            $minHeight={minHeight}
            $maxHeight={maxHeight}
            coloredBackground={props.coloredBackground}
            {...props}
            onChange={(e) => {
              props.onChange && props.onChange(e)
              if (markdown) recomputeMarks()
            }}
            onSelect={markdown ? recomputeMarks : undefined}
            withButton={
              props.button !== undefined || props.maxLength !== undefined
            }
            onBlur={(e) => {
              props.handleBlur && props.handleBlur(e)
            }}
          />
        )}
        {props.button && (
          <StyledLink
            caption={props.button.label}
            onClick={props.button.handleClick}
            disabled={props.button.disabled}
            disableUnderline
            color="primary"
          />
        )}
        {props.maxLength && !preview && (
          <StyledText
            color={
              props.value &&
              typeof props.value === 'string' &&
              props.value.length === props.maxLength
                ? 'secondary'
                : 'textLighter'
            }
            textStyle="label"
          >
            {(props.value &&
              typeof props.value === 'string' &&
              props.value.length) ||
              0}{' '}
            / {props.maxLength}
          </StyledText>
        )}
      </InnerWrapper>
    )

    const toolbar = (
      <MarkdownToolbar
        activeMarks={marks}
        preview={preview}
        showHeading={markdownHeading}
        bottom={markdownToolbarBottom}
        onInline={handleInline}
        onBlock={handleBlock}
        onTogglePreview={handleTogglePreview}
      />
    )

    return (
      <TextareaWrapper
        className={className}
        disabled={props.disabled}
        errorMessage={errorMessage}
      >
        {props.label && (
          <LabelWrapper>
            {props.label && (
              <TextareaLabel tag="label" textStyle={'labelTinyCaps'}>
                {props.label}
              </TextareaLabel>
            )}
            {props.iconType && (
              <CircleIcon
                type={props.iconType}
                size={16}
                color={themeContext.colors.textLight}
                hoverText={props.iconHoverText}
              />
            )}
          </LabelWrapper>
        )}
        {props.label && props.additionalLabel && (
          <Label
            textStyle="caption"
            color="textLight"
            marginTop={-4}
            marginBottom={8}
          >
            {props.additionalLabel}
          </Label>
        )}
        {markdown ? (
          <MarkdownContainer $plain={markdownPlain}>
            {!markdownToolbarBottom && toolbar}
            {inner}
            {markdownToolbarBottom && toolbar}
          </MarkdownContainer>
        ) : (
          inner
        )}
        <TextareaError>{errorMessage}</TextareaError>
      </TextareaWrapper>
    )
  }
)

const TextareaLabel = styled(Text)`
  display: block;
  padding-left: 1px;
  ${applyTextEllipsis()}
`

const StyledAvatar = styled(Avatar)`
  margin-right: 8px;
  flex-shrink: 0;
`

const Label = styled(Text)`
  padding-left: 1px;
  ${applyTextEllipsis()}
`

const InnerWrapper = styled.div`
  position: relative;
  display: flex;
`

const StyledLink = styled(Link)`
  position: absolute;
  right: 16px;
  bottom: 8px;
  transform: translateY(-50%);
`

const StyledText = styled(Text)`
  position: absolute;
  right: 16px;
  bottom: 16px;
`

const StyledTextarea = styled.textarea<{
  $minHeight: number
  $maxHeight: number
  withButton?: boolean
  coloredBackground?: boolean
}>`
  min-height: ${({ $minHeight }) => $minHeight}px;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  display: block;
  resize: vertical;
  overflow-y: auto;
  width: 100%;
  padding: ${({ withButton }) =>
    withButton ? '12px 104px 12px 16px' : '12px 16px'};
  border: 1px solid ${({ theme }) => theme.colors.textLighter};
  border-radius: 8px;
  outline: 0;
  box-shadow: ${shadows.depthOne};
  background-color: ${({ theme, coloredBackground }) =>
    coloredBackground ? theme.colors.primaryLight : theme.colors.lightest};
  ${applyTextStyle('bodySmall')}
  line-height: 22px;
  color: ${({ theme }) => theme.colors.text};
  transition: border-color 0.3s ease;

  &:disabled {
    border-color: ${({ theme }) => theme.colors.light};
    pointer-events: none;
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.textLighter};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  /* Slim scrollbar inset from the rounded corners, so it never covers the border */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.textLighter} transparent;

  &::-webkit-scrollbar {
    width: 14px;
  }
  &::-webkit-scrollbar-button {
    display: none;
  }
  &::-webkit-scrollbar-track {
    margin: 10px 0;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.textLighter};
    border-radius: 8px;
    border: 4px solid transparent;
    background-clip: padding-box;
  }
`

const TextareaWrapper = styled.div<
  Pick<IFormControlProps, 'disabled' | 'errorMessage'>
>`
  position: relative;
  display: block;

  ${(props) =>
    props.errorMessage &&
    css`
      ${StyledTextarea} {
        border-color: ${props.theme.colors.secondary};
      }

      ${TextareaError} {
        display: block;
      }
    `}

  ${(props) =>
    props.disabled &&
    css`
      ${TextareaLabel}, ${Label} {
      color: ${props.theme.colors.textLighter};
    `}
`

const LabelWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

const TextareaError = styled.p`
  display: none;
  width: 100%;
  margin: 8px 0 0 0;
  padding-left: 1px;
  color: ${({ theme }) => theme.colors.secondary};
  ${applyTextStyle('label')}
  ${applyTextEllipsis()}
`

// Wraps the toolbar + field into a single rounded control. The inner
// textarea drops its own border/shadow so the two read as one. The border uses
// the same textLighter as Input/Textarea/Dropdown, so a markdown field does not
// read as a lighter box than the plain fields next to it in a form.
const MarkdownContainer = styled.div<{ $plain?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.textLighter};
  border-radius: 8px;
  background-color: ${({ theme, $plain }) =>
    $plain ? theme.colors.lightest : theme.colors.primaryLight};
  box-shadow: ${shadows.depthOne};
  overflow: hidden;
  transition: border-color 0.3s ease;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  ${StyledTextarea}, ${StyledTextarea}:hover, ${StyledTextarea}:focus {
    border: none;
    box-shadow: none;
    background-color: transparent;
  }
`

const PreviewBox = styled.div`
  width: 100%;
  padding: 12px 16px;
`

export default Textarea
